<template>
  <b-modal
    :visible="visible"
    :title="title"
    @hidden="$emit('close')"
    @ok.prevent="submit"
    :ok-disabled="isSubmitting"
    :cancel-disabled="isSubmitting"
  >
    <b-form @submit.prevent="submit" novalidate>
      <b-form-group label="Type">
        <b-form-select v-model="form.type" :options="adjustmentTypeOptions" required />
        <b-form-text v-if="form.type === 1">Reset Value resets the current value to the specified value.</b-form-text>
      </b-form-group>

      <b-form-group
        label="Adjustment Value (L)"
        label-for="adjustment-value"
        :invalid-feedback="amountInvalidFeedback"
        :state="amountState"
      >
        <NumberInput
          id="adjustment-value"
          v-model="form.amount"
          class="form-control"
          :class="{ 'is-invalid': amountState === false, 'is-valid': amountState === true }"
          placeholder="Use negative value for subtraction"
          required
        />
      </b-form-group>

      <b-form-group
        label="Adjustment Reason"
        label-for="adjustment-reason"
        invalid-feedback="Adjustment reason is required"
        :state="reasonState"
      >
        <b-form-select
          id="adjustment-reason"
          v-model="form.reason"
          :options="reasonOptions"
          :state="reasonState"
          required
        ></b-form-select>
      </b-form-group>

      <b-form-group
        label-for="adjustment-note"
        :invalid-feedback="noteInvalidFeedback"
        :state="noteState"
      >
        <template #label>
          <div class="d-flex justify-content-between">
            <span>Detail Note</span>
            <small class="text-muted">{{ form.note.length }} / 255</small>
          </div>
        </template>
        <b-form-textarea
          id="adjustment-note"
          v-model="form.note"
          placeholder="Explain the details of this adjustment..."
          rows="3"
          :state="noteState"
          required
          maxlength="255"
          no-resize
        ></b-form-textarea>
      </b-form-group>
    </b-form>
    <template #modal-footer="{ ok, cancel, disabled }">
      <b-button @click="cancel()" :disabled="disabled">Cancel</b-button>
      <b-button @click="ok()" variant="primary" :disabled="disabled">
        <b-spinner v-if="isSubmitting" small></b-spinner>
        Save
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import NumberInput from '@/components/directives/NumberInput.vue';
import { ADJUSTMENT_TYPE_OPTIONS } from '@/util/adjustment';

export default {
  name: 'AdjustmentForm',
  components: { NumberInput },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: {
        id: null,
        amount: null,
        reason: null,
        note: null,
        type: 1,
      },
      submitted: false,
      reasonOptions: [
        { value: null, text: '-- Select Reason --' },
        { value: 'MANUAL_ADDITION', text: 'Manual Addition (Pipe Leak, etc.)' },
        { value: 'LOSS_DUE_TO_SPILLAGE', text: 'Loss due to Spillage' },
        { value: 'METER_CALIBRATION_ERROR', text: 'Meter Calibration Error' },
        { value: 'Dispute_WITH_SUPPLIER', text: 'Dispute with Supplier' },
        { value: 'OTHERS', text: 'Others' },
      ],
    };
  },
  computed: {
    adjustmentTypeOptions() {
      return ADJUSTMENT_TYPE_OPTIONS;
    },
    title() {
      return this.initialData ? 'Edit Bunker Adjustment' : 'Create Bunker Adjustment';
    },
    amountState() {
      if (!this.submitted) return null;
      return this.form.amount !== null && this.form.amount !== 0;
    },
    amountInvalidFeedback() {
      if (this.form.amount === null || this.form.amount === '') {
        return 'Adjustment value is required.';
      }
      if (this.form.amount === 0) {
        return 'Adjustment value cannot be zero.';
      }
      return '';
    },
    reasonState() {
      if (!this.submitted) return null;
      return !!this.form.reason;
    },
    noteInvalidFeedback() {
      if (this.form.note && this.form.note.length > 255) {
        return 'Note cannot exceed 255 characters.';
      }
      if (!this.form.note || this.form.note.trim() === '') {
        return 'Detail note is required.';
      }
      return '';
    },
    noteState() {
      if (!this.submitted) return null;

      if (!this.form.note) {
        return false;
      }

      if (this.form.note && this.form.note.length > 255) {
        return false;
      }

      return this.form.note && this.form.note.trim() !== '';
    },
  },
  methods: {
    validateForm() {
      this.submitted = true;
      return this.amountState !== false && this.reasonState !== false && this.noteState !== false;
    },
    submit() {
      if (this.validateForm()) {
        this.$emit('submit', this.form);
      }
    },
    resetForm() {
      this.submitted = false;
      if (this.initialData) {
        this.form = {
          id: this.initialData.id,
          amount: this.initialData.amount,
          reason: this.initialData.reasonValue,
          note: this.initialData.fullNote || '',
          type: this.initialData.type,
        };
      } else {
        this.form = {
          id: null,
          amount: null,
          reason: null,
          note: '',
          type: 1,
        };
      }
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm();
      }
    },
  },
};
</script>
